import type {
  Branding,
  DirectSalePriceQuote,
  Motif,
  PlacementMm,
} from '@designdeck/shared';
import apiClient, { ApiResponse } from './client';
import { applyBranding } from '@/lib/theme';
import type {
  AvailableDecorationMethod,
  DecorationMethod,
  Product,
  ProductCategory,
} from './products';

/**
 * Direct Sale API surface.
 *
 * Every call carries the embedding shop's signature from the iframe URL — the
 * end customer has no account, so the shop vouches for the session and the
 * server re-verifies on each request. The signature is read from the URL on
 * each call rather than captured once, so it always matches what the page was
 * actually opened with.
 */

export interface DirectSaleAccessParams {
  shop: string;
  ts: string;
  sig: string;
}

/** Reads the shop signature the embedding page put on the iframe URL. */
export function getAccessParams(): DirectSaleAccessParams | null {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  const shop = params.get('shop');
  const ts = params.get('ts');
  const sig = params.get('sig');

  if (!shop || !ts || !sig) return null;
  return { shop, ts, sig };
}

/**
 * The embedding shop's own correlation id (?sessionRef=…) — its customer or
 * cart reference. Carried through session and handover verbatim; DesignDeck
 * attaches no meaning to it.
 */
export function getSessionRef(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('sessionRef');
}

function withAccess(params: URLSearchParams): string {
  const access = getAccessParams();
  if (access) {
    params.set('shop', access.shop);
    params.set('ts', access.ts);
    params.set('sig', access.sig);
  }
  return params.toString();
}

// ---------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------

export const DIRECT_SALE_SESSION_HEADER = 'x-direct-sale-session';

interface DirectSaleSession {
  token: string;
  expiresAt: string;
  /** The shop's configured embedding origin — postMessage target for the
   * checkout handover. null when the shop has not configured one. */
  embedOrigin: string | null;
  /** The shop's branding colours; null for the reference design. */
  branding?: Branding | null;
}

/**
 * Module-level, deliberately NOT in tokenStorage: the seller flow's axios
 * refresh machinery must never see this token, and it must never ride on
 * non-Direct-Sale requests.
 */
let session: DirectSaleSession | null = null;
let sessionExchange: Promise<DirectSaleSession> | null = null;

/** Carries the seller's 30-day test token on the configurator URL. */
export const TEST_LINK_PARAM = 'try';
/** Entries older than this are renewed before the exchange (guard: 300 s). */
const ENTRY_FRESH_SECONDS = 240;

/**
 * A test link has no embedding page that signs a fresh entry on every load:
 * the signature on the URL is the one redeemed when the link was opened.
 * A reload after five minutes — or Safari restoring a discarded tab, or the
 * URL shared on from the address bar — would present it expired. So when
 * the URL carries the test token and its entry is getting old, redeem the
 * token again and swap the fresh signature into the URL first. Best effort:
 * if that fails, the exchange runs with what is there and fails as before.
 */
async function renewTestEntry(): Promise<void> {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const token = params.get(TEST_LINK_PARAM);
  if (!token) return;
  const ts = Number(params.get('ts'));
  if (Number.isFinite(ts) && Date.now() / 1000 - ts < ENTRY_FRESH_SECONDS) {
    return;
  }

  try {
    const entry = await redeemTestLink(token);
    params.set('shop', entry.shop);
    params.set('ts', String(entry.ts));
    params.set('sig', entry.sig);
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}?${params.toString()}`
    );
  } catch {
    // Expired or withdrawn: the exchange below reports it the usual way.
  }
}

/**
 * Exchanges the URL signature (valid ±300s) for an 8h session token once,
 * then reuses it. Concurrent callers share one in-flight exchange. After
 * this, no Direct Sale XHR carries the signature in its URL — which also
 * keeps the credential out of access logs.
 */
export async function ensureSession(): Promise<DirectSaleSession> {
  if (session && new Date(session.expiresAt).getTime() > Date.now()) {
    return session;
  }

  if (!sessionExchange) {
    const params = new URLSearchParams();
    const ref = getSessionRef();
    if (ref) params.set('sessionRef', ref);

    sessionExchange = renewTestEntry()
      .then(() =>
        apiClient.post<ApiResponse<DirectSaleSession>>(
          `/products/direct-sale/session?${withAccess(params)}`
        )
      )
      .then((response) => {
        session = response.data.data;
        // The shop's colours arrive with the session; URL overrides on the
        // iframe URL keep winning inside applyBranding.
        applyBranding(session.branding ?? null);
        return session;
      })
      .finally(() => {
        sessionExchange = null;
      });
  }

  return sessionExchange;
}

/** Test seam: drops the cached session so the next call re-exchanges. */
export function clearSession(): void {
  session = null;
  sessionExchange = null;
}

/**
 * The shop's configured embedding origin from the current session, when the
 * exchange has completed. Synchronous on purpose — the checkout click
 * handler needs it without awaiting a network call.
 */
export function getEmbedOrigin(): string | null {
  return session?.embedOrigin ?? null;
}

/**
 * Catalogue for step 1. Session-bound: the server resolves the embedding
 * shop from the token and answers with the products that shop's Sales
 * Channel offers, instead of the whole public storefront.
 */
async function sessionHeaders(): Promise<Record<string, string>> {
  const { token } = await ensureSession();
  return { [DIRECT_SALE_SESSION_HEADER]: token };
}

export async function getCatalogProducts(
  categoryId?: string
): Promise<Product[]> {
  const params = new URLSearchParams();
  if (categoryId) params.set('categoryId', categoryId);

  const response = await apiClient.get<ApiResponse<Product[]>>(
    `/products/direct-sale/catalog?${params.toString()}`,
    { headers: await sessionHeaders() }
  );
  return response.data.data;
}

export async function getCatalogCategories(): Promise<ProductCategory[]> {
  const response = await apiClient.get<ApiResponse<ProductCategory[]>>(
    '/products/direct-sale/catalog/categories',
    { headers: await sessionHeaders() }
  );
  return response.data.data;
}

/** A raw `PositionDecorationMethod` row, as the detail endpoint answers it. */
interface DecorationMethodJoinRow {
  isDefault?: boolean;
  decorationMethod?: (DecorationMethod & AvailableDecorationMethod) | null;
  /** Price with this method on the position; null: not sold. */
  directSalePrice?: string | null;
}

/**
 * The detail endpoint answers each position's `decorationMethods` as raw
 * join rows; the list endpoint flattens them to the one default method plus
 * `availableDecorationMethods`. Direct Sale seeds the shared products cache
 * with this product, and every reader of that cache — the editor's
 * embroidery rule first of all — expects the list shape. Left as rows, an
 * embroidered product was stacked like a printed one: garment shading over
 * the thread instead of under it.
 *
 * Same rule as the server's list transform: only a flagged row is the
 * default, an unflagged first row never becomes one by position.
 */
export function toCatalogListShape(product: Product): Product {
  return {
    ...product,
    positions: product.positions?.map((position) => {
      const rows = position.decorationMethods as unknown;
      if (!Array.isArray(rows)) return position;

      const joinRows = rows as DecorationMethodJoinRow[];
      return {
        ...position,
        decorationMethods:
          joinRows.find((row) => row.isDefault)?.decorationMethod ?? null,
        availableDecorationMethods: joinRows.flatMap(
          ({ decorationMethod: method, directSalePrice }) =>
            method
              ? [
                  {
                    id: method.id,
                    code: method.code,
                    name: method.name,
                    isActive: method.isActive,
                    maxColors: method.maxColors ?? null,
                    minDpi: method.minDpi ?? null,
                    ...(directSalePrice !== undefined
                      ? { directSalePrice }
                      : {}),
                  },
                ]
              : []
        ),
      };
    }),
  };
}

export async function getCatalogProduct(id: string): Promise<Product> {
  const response = await apiClient.get<ApiResponse<Product>>(
    `/products/direct-sale/catalog/${id}`,
    { headers: await sessionHeaders() }
  );
  return toCatalogListShape(response.data.data);
}

/**
 * Curated motif library for the design step's motif panel. `fileUrl` is
 * rebuilt against the client's API base URL (the server returns an
 * API-relative path) so the canvas can fetch the image cross-origin with
 * the API's CORS headers — a prerequisite for untainted preview exports.
 */
export async function getMotifs(): Promise<Motif[]> {
  const response =
    await apiClient.get<ApiResponse<Motif[]>>('/motifs/storefront');
  const base = apiClient.defaults.baseURL ?? '';
  return response.data.data.map((motif) => ({
    ...motif,
    fileUrl: `${base}/motifs/storefront/${motif.id}/file`,
  }));
}

export interface DirectSaleQuoteRequest {
  productId: string;
  colorId?: string | null;
  /** One piece of this size; ignored when `items` is sent. */
  sizeId?: string | null;
  /** Sizes with quantities. */
  items?: { sizeId: string; quantity: number }[];
  positions: {
    positionId: string;
    widthMm?: number | null;
    heightMm?: number | null;
  }[];
  /** Decoration method for the whole product; omitted keeps each default. */
  decorationMethodId?: string | null;
  language?: string;
}

/**
 * Server-calculated price breakdown. The frontend renders this verbatim and
 * never recalculates, so the displayed amount and the amount handed to the
 * buying system cannot diverge. Authenticated by the session token in a
 * header — the URL signature only opens the session exchange.
 */
export async function getPriceQuote(
  request: DirectSaleQuoteRequest
): Promise<DirectSalePriceQuote> {
  const { token } = await ensureSession();
  const response = await apiClient.post<ApiResponse<DirectSalePriceQuote>>(
    '/products/direct-sale/quote',
    request,
    { headers: { [DIRECT_SALE_SESSION_HEADER]: token } }
  );
  return response.data.data;
}

/** One decorated position as the commit sends it. */
export interface DirectSaleCommitPosition {
  positionSlug: string;
  /** Canvas JSON in millimetre space on the position's print area. */
  canvasData: Record<string, unknown>;
  /** The design box in mm on the position's print area (required, R17). */
  placementMm: PlacementMm;
  designCropUrl: string | null;
}

export interface DirectSaleCommitRequest {
  commitKey: string;
  /** The quote shown; its redemption returns the committed design. */
  quoteId: string;
  productId: string;
  colorId: string | null;
  sizeId: string | null;
  positions: DirectSaleCommitPosition[];
}

export interface DirectSaleCommitResult {
  designId: string;
  runId: string;
  /** Over the session limit: the session's latest design was reused. */
  reused?: boolean;
}

/**
 * Turns the configuration into a design of the shop and freezes its print
 * data, once: the same commit key returns the same design. Called when the
 * customer clicks "Kaufen", before the handover; pricing calls never create
 * a design. Fails with a coded error (quote_not_found,
 * quote_already_redeemed, quote_already_linked, quote_design_mismatch,
 * direct_sale_commit_limit …).
 */
export async function commitDirectSaleDesign(
  request: DirectSaleCommitRequest
): Promise<DirectSaleCommitResult> {
  const { token } = await ensureSession();
  const response = await apiClient.post<ApiResponse<DirectSaleCommitResult>>(
    '/products/direct-sale/commit',
    request,
    { headers: { [DIRECT_SALE_SESSION_HEADER]: token } }
  );
  return response.data.data;
}

// ---------------------------------------------------------------------
// Seller preview
// ---------------------------------------------------------------------

/** Mirrors DirectSalePreviewLink in apps/api (direct-sale-preview.service). */
export interface DirectSalePreviewLink {
  available: boolean;
  /** The seller's own shop, or the demo shop when they have none. */
  ownShop?: boolean;
  shop?: string;
  ts?: number;
  sig?: string;
}

/**
 * A signed Direct Sale entry link for the logged-in seller, valid for five
 * minutes. Called from the seller's area with the seller's token — not from
 * inside the Direct Sale flow.
 */
export async function getPreviewLink(): Promise<DirectSalePreviewLink> {
  const response = await apiClient.get<ApiResponse<DirectSalePreviewLink>>(
    '/products/direct-sale/preview-link'
  );
  return response.data.data;
}

/** Mirrors DirectSaleTestLink in apps/api (direct-sale-preview.service). */
export interface DirectSaleTestLink {
  available: boolean;
  ownShop?: boolean;
  token?: string;
  /** Unix seconds. */
  expiresAt?: number;
}

/**
 * A shareable 30-day test token for the logged-in seller; the public page
 * /try/<token> opens the configurator with it on any device.
 */
export async function createTestLink(): Promise<DirectSaleTestLink> {
  const response = await apiClient.post<ApiResponse<DirectSaleTestLink>>(
    '/products/direct-sale/test-link'
  );
  return response.data.data;
}

/**
 * Withdraws every test link of the seller's own shop (a no-op — `revoked:
 * false` — for a seller who previews the demo shop).
 */
export async function revokeTestLinks(): Promise<{ revoked: boolean }> {
  const response = await apiClient.post<ApiResponse<{ revoked: boolean }>>(
    '/products/direct-sale/test-link/revoke'
  );
  return response.data.data;
}

/**
 * Trades a test token for a fresh entry link. Public — called from the test
 * page, which has no session of any kind. Rejects (404) for an expired,
 * forged or revoked token.
 */
export async function redeemTestLink(
  token: string
): Promise<{ shop: string; ts: number; sig: string }> {
  const response = await apiClient.get<
    ApiResponse<{ shop: string; ts: number; sig: string }>
  >('/products/direct-sale/test-entry', { params: { t: token } });
  return response.data.data;
}
